import React, { useState, useEffect, useRef } from 'react';
import {
    MessageCircle,
    Send,
    ArrowLeft,
    Target,
    Users,
    Globe,
    DollarSign,
    Image,
    Rocket,
    CheckCircle,
    Loader2,
    AlertCircle,
    ChevronRight,
    Sparkles,
    Calendar,
    Layers
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { createCampaign, validateCampaign, getProjectedPerformance } from '../services/campaignActionsService';
import { formatCurrency } from '../utils/helpers';

// Campaign Builder Phases
const PHASES = {
    WELCOME: 'welcome',
    OBJECTIVE: 'objective',
    BUDGET: 'budget',
    AUDIENCE: 'audience',
    PLACEMENTS: 'placements',
    CREATIVE: 'creative',
    REVIEW: 'review',
    LAUNCHING: 'launching',
    COMPLETE: 'complete'
};

const PHASE_ORDER = [
    PHASES.WELCOME,
    PHASES.OBJECTIVE,
    PHASES.BUDGET,
    PHASES.AUDIENCE,
    PHASES.PLACEMENTS,
    PHASES.CREATIVE,
    PHASES.REVIEW,
    PHASES.LAUNCHING,
    PHASES.COMPLETE
];

const OBJECTIVES = [
    { id: 'awareness', label: 'Brand Awareness', description: 'Get your brand in front of more people', icon: Target },
    { id: 'traffic', label: 'Website Traffic', description: 'Drive visitors to your website', icon: Globe },
    { id: 'engagement', label: 'Engagement', description: 'Get more likes, comments, and shares', icon: MessageCircle },
    { id: 'leads', label: 'Lead Generation', description: 'Collect contact information from interested people', icon: Users },
    { id: 'sales', label: 'Sales/Conversions', description: 'Get people to purchase your products', icon: DollarSign }
];

const CampaignBuilder = () => {
    const { selectedBrand } = useApp();
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const messagesEndRef = useRef(null);

    const [currentPhase, setCurrentPhase] = useState(PHASES.WELCOME);
    const [messages, setMessages] = useState([]);
    const [userInput, setUserInput] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [campaignConfig, setCampaignConfig] = useState({
        name: '',
        objective: null,
        budget: { amount: 50, type: 'daily' },
        duration: { startDate: null, endDate: null },
        targeting: {
            locations: { countries: ['US'] },
            ageMin: 18,
            ageMax: 65,
            genders: []
        },
        placements: 'automatic',
        creativeId: null,
        creative: null
    });
    const [validation, setValidation] = useState({ errors: [], warnings: [] });
    const [projection, setProjection] = useState(null);
    const [launching, setLaunching] = useState(false);

    // Initialize with welcome message
    useEffect(() => {
        if (selectedBrand && messages.length === 0) {
            addAIMessage(`Hey! I'm here to help you create a new campaign for ${selectedBrand.name}. Let's build something great together! First, what would you like this campaign to achieve?`);
            setCurrentPhase(PHASES.OBJECTIVE);
        }
    }, [selectedBrand]);

    // Auto-scroll to bottom
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const addAIMessage = (text, options = null, type = 'text') => {
        setIsTyping(true);
        setTimeout(() => {
            setMessages(prev => [...prev, {
                id: Date.now(),
                sender: 'ai',
                text,
                options,
                type,
                timestamp: new Date()
            }]);
            setIsTyping(false);
        }, 500);
    };

    const addUserMessage = (text) => {
        setMessages(prev => [...prev, {
            id: Date.now(),
            sender: 'user',
            text,
            timestamp: new Date()
        }]);
    };

    const handleObjectiveSelect = (objective) => {
        addUserMessage(objective.label);
        setCampaignConfig(prev => ({ ...prev, objective: objective.id }));

        setTimeout(() => {
            addAIMessage(
                `Great choice! ${objective.description}. Now let's talk budget. How much would you like to spend per day? I'd recommend starting with at least $20/day for meaningful results.`,
                null,
                'text'
            );
            setCurrentPhase(PHASES.BUDGET);
        }, 300);
    };

    const handleBudgetSubmit = () => {
        const budget = parseFloat(userInput) || campaignConfig.budget.amount;
        addUserMessage(`$${budget}/day`);
        setCampaignConfig(prev => ({
            ...prev,
            budget: { amount: budget, type: 'daily' }
        }));
        setUserInput('');

        setTimeout(() => {
            addAIMessage(
                `Perfect, $${budget}/day works. Now, who do you want to reach? Let's define your target audience. Which countries should we target?`,
                [
                    { id: 'US', label: 'United States' },
                    { id: 'CA', label: 'Canada' },
                    { id: 'GB', label: 'United Kingdom' },
                    { id: 'AU', label: 'Australia' },
                    { id: 'global', label: 'All Countries' }
                ],
                'multi-select'
            );
            setCurrentPhase(PHASES.AUDIENCE);
        }, 300);
    };

    const handleAudienceSelect = (countries) => {
        const selectedCountries = countries === 'global' ? [] : [countries];
        addUserMessage(countries === 'global' ? 'All Countries' : countries);

        setCampaignConfig(prev => ({
            ...prev,
            targeting: {
                ...prev.targeting,
                locations: { countries: selectedCountries }
            }
        }));

        setTimeout(() => {
            addAIMessage(
                `Got it! Now for placements - where should your ads appear? I recommend Automatic Placements (Advantage+) to let Meta optimize, but you can choose manual if you prefer.`,
                [
                    { id: 'automatic', label: 'Automatic (Recommended)', description: 'Let Meta optimize across all placements' },
                    { id: 'manual', label: 'Manual Selection', description: 'Choose specific placements' }
                ],
                'single-select'
            );
            setCurrentPhase(PHASES.PLACEMENTS);
        }, 300);
    };

    const handlePlacementSelect = (placement) => {
        addUserMessage(placement === 'automatic' ? 'Automatic Placements' : 'Manual Selection');
        setCampaignConfig(prev => ({ ...prev, placements: placement }));

        setTimeout(() => {
            addAIMessage(
                `Smart choice! Now for the creative - do you want to select an existing creative from your library, or generate a new one?`,
                [
                    { id: 'library', label: 'Select from Library', icon: Image },
                    { id: 'generate', label: 'Generate New Creative', icon: Sparkles }
                ],
                'single-select'
            );
            setCurrentPhase(PHASES.CREATIVE);
        }, 300);
    };

    const handleCreativeSelect = (choice) => {
        addUserMessage(choice === 'library' ? 'Select from Library' : 'Generate New');

        if (choice === 'generate') {
            // Navigate to creative generation
            navigate('/conversational');
            return;
        }

        // For now, skip to review
        setTimeout(() => {
            addAIMessage(
                `You can select a creative from your library later. Let's review your campaign setup!`,
                null,
                'text'
            );
            goToReview();
        }, 300);
    };

    const goToReview = async () => {
        setCurrentPhase(PHASES.REVIEW);

        // Validate configuration
        try {
            const validationResult = await validateCampaign(
                campaignConfig,
                currentUser.uid,
                selectedBrand.id
            );
            setValidation(validationResult);
        } catch (error) {
            console.error('Validation error:', error);
        }

        // Get projected performance
        try {
            const projectionResult = await getProjectedPerformance(
                campaignConfig,
                currentUser.uid,
                selectedBrand.id
            );
            setProjection(projectionResult);
        } catch (error) {
            console.error('Projection error:', error);
        }

        addAIMessage(
            `Here's your campaign summary. Take a look and let me know if you want to make any changes!`,
            null,
            'review'
        );
    };

    const handleLaunch = async () => {
        setLaunching(true);
        setCurrentPhase(PHASES.LAUNCHING);

        addAIMessage(`Launching your campaign... This may take a moment.`, null, 'text');

        try {
            const result = await createCampaign(
                {
                    ...campaignConfig,
                    platform: 'meta',
                    name: campaignConfig.name || `${selectedBrand.name} - ${new Date().toLocaleDateString()}`
                },
                currentUser.uid,
                selectedBrand.id
            );

            setCurrentPhase(PHASES.COMPLETE);
            addAIMessage(
                `Your campaign has been created successfully! It's currently paused so you can review everything before it goes live. Head to the Campaigns page to activate it when you're ready.`,
                null,
                'success'
            );
        } catch (error) {
            console.error('Launch error:', error);
            addAIMessage(
                `Oops! There was an issue creating your campaign: ${error.message}. Please try again or contact support.`,
                null,
                'error'
            );
        } finally {
            setLaunching(false);
        }
    };

    const handleSendMessage = () => {
        if (!userInput.trim()) return;

        if (currentPhase === PHASES.BUDGET) {
            handleBudgetSubmit();
        } else {
            addUserMessage(userInput);
            setUserInput('');
            // Handle free-form input based on context
        }
    };

    const getPhaseProgress = () => {
        const currentIndex = PHASE_ORDER.indexOf(currentPhase);
        return Math.round((currentIndex / (PHASE_ORDER.length - 1)) * 100);
    };

    if (!selectedBrand) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '60vh',
                gap: 'var(--space-4)'
            }}>
                <Rocket size={48} style={{ color: 'var(--color-text-tertiary)' }} />
                <h2 style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
                    Select a brand to create a campaign
                </h2>
            </div>
        );
    }

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - 80px)',
            maxWidth: '900px',
            margin: '0 auto'
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-4)',
                padding: 'var(--space-4) 0',
                borderBottom: '1px solid var(--color-border)'
            }}>
                <button
                    onClick={() => navigate('/command-center')}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--color-text-secondary)',
                        cursor: 'pointer',
                        padding: 'var(--space-2)'
                    }}
                >
                    <ArrowLeft size={20} />
                </button>
                <div style={{ flex: 1 }}>
                    <h1 style={{
                        fontSize: 'var(--font-size-xl)',
                        fontWeight: 600,
                        margin: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)'
                    }}>
                        <Rocket size={24} style={{ color: 'var(--color-primary)' }} />
                        Campaign Builder
                    </h1>
                    <p style={{
                        margin: 0,
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--color-text-tertiary)'
                    }}>
                        {selectedBrand.name}
                    </p>
                </div>

                {/* Progress Indicator */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)'
                }}>
                    <div style={{
                        width: '100px',
                        height: '4px',
                        background: 'var(--color-surface)',
                        borderRadius: 'var(--radius-full)',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            width: `${getPhaseProgress()}%`,
                            height: '100%',
                            background: 'var(--gradient-primary)',
                            transition: 'width 0.3s ease'
                        }} />
                    </div>
                    <span style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-tertiary)'
                    }}>
                        {getPhaseProgress()}%
                    </span>
                </div>
            </div>

            {/* Chat Area */}
            <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: 'var(--space-4)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-4)'
            }}>
                {messages.map((message) => (
                    <ChatMessage
                        key={message.id}
                        message={message}
                        onOptionSelect={(option) => {
                            if (currentPhase === PHASES.OBJECTIVE) {
                                const obj = OBJECTIVES.find(o => o.id === option);
                                if (obj) handleObjectiveSelect(obj);
                            } else if (currentPhase === PHASES.AUDIENCE) {
                                handleAudienceSelect(option);
                            } else if (currentPhase === PHASES.PLACEMENTS) {
                                handlePlacementSelect(option);
                            } else if (currentPhase === PHASES.CREATIVE) {
                                handleCreativeSelect(option);
                            }
                        }}
                        campaignConfig={campaignConfig}
                        projection={projection}
                        validation={validation}
                        onLaunch={handleLaunch}
                        launching={launching}
                    />
                ))}

                {/* Objective Selection */}
                {currentPhase === PHASES.OBJECTIVE && messages.length > 0 && (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                        gap: 'var(--space-3)'
                    }}>
                        {OBJECTIVES.map((obj) => (
                            <ObjectiveCard
                                key={obj.id}
                                objective={obj}
                                onSelect={() => handleObjectiveSelect(obj)}
                            />
                        ))}
                    </div>
                )}

                {isTyping && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        color: 'var(--color-text-tertiary)',
                        fontSize: 'var(--font-size-sm)'
                    }}>
                        <Loader2 size={16} className="spin" />
                        AI is typing...
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            {currentPhase !== PHASES.REVIEW && currentPhase !== PHASES.COMPLETE && (
                <div style={{
                    padding: 'var(--space-4)',
                    borderTop: '1px solid var(--color-border)',
                    background: 'var(--color-bg-secondary)'
                }}>
                    <div style={{
                        display: 'flex',
                        gap: 'var(--space-3)',
                        alignItems: 'center'
                    }}>
                        <input
                            type={currentPhase === PHASES.BUDGET ? 'number' : 'text'}
                            value={userInput}
                            onChange={(e) => setUserInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                            placeholder={
                                currentPhase === PHASES.BUDGET
                                    ? 'Enter daily budget (e.g., 50)'
                                    : 'Type your message...'
                            }
                            style={{
                                flex: 1,
                                padding: 'var(--space-3) var(--space-4)',
                                borderRadius: 'var(--radius-lg)',
                                border: '1px solid var(--color-border)',
                                background: 'var(--color-surface)',
                                color: 'var(--color-text-primary)',
                                fontSize: 'var(--font-size-md)'
                            }}
                        />
                        <button
                            onClick={handleSendMessage}
                            disabled={!userInput.trim()}
                            style={{
                                padding: 'var(--space-3)',
                                borderRadius: 'var(--radius-lg)',
                                background: userInput.trim() ? 'var(--gradient-primary)' : 'var(--color-surface)',
                                border: 'none',
                                color: userInput.trim() ? 'white' : 'var(--color-text-tertiary)',
                                cursor: userInput.trim() ? 'pointer' : 'not-allowed'
                            }}
                        >
                            <Send size={20} />
                        </button>
                    </div>
                </div>
            )}

            <style>{`
                .spin {
                    animation: spin 1s linear infinite;
                }
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

// Chat Message Component
const ChatMessage = ({ message, onOptionSelect, campaignConfig, projection, validation, onLaunch, launching }) => {
    const isAI = message.sender === 'ai';

    return (
        <div style={{
            display: 'flex',
            justifyContent: isAI ? 'flex-start' : 'flex-end'
        }}>
            <div style={{
                maxWidth: '80%',
                padding: 'var(--space-4)',
                borderRadius: 'var(--radius-lg)',
                background: isAI ? 'var(--color-surface)' : 'var(--gradient-primary)',
                color: isAI ? 'var(--color-text-primary)' : 'white',
                boxShadow: 'var(--shadow-sm)'
            }}>
                {isAI && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        marginBottom: 'var(--space-2)',
                        color: 'var(--color-primary)'
                    }}>
                        <Sparkles size={16} />
                        <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600 }}>AI Assistant</span>
                    </div>
                )}

                <p style={{ margin: 0, lineHeight: 1.5 }}>{message.text}</p>

                {/* Options */}
                {message.options && message.type === 'single-select' && (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'var(--space-2)',
                        marginTop: 'var(--space-3)'
                    }}>
                        {message.options.map((opt) => (
                            <button
                                key={opt.id}
                                onClick={() => onOptionSelect(opt.id)}
                                style={{
                                    padding: 'var(--space-3)',
                                    borderRadius: 'var(--radius-md)',
                                    border: '1px solid var(--color-border)',
                                    background: 'var(--color-bg-primary)',
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                    transition: 'all var(--transition-fast)'
                                }}
                            >
                                <div style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>{opt.label}</div>
                                {opt.description && (
                                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                                        {opt.description}
                                    </div>
                                )}
                            </button>
                        ))}
                    </div>
                )}

                {message.options && message.type === 'multi-select' && (
                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 'var(--space-2)',
                        marginTop: 'var(--space-3)'
                    }}>
                        {message.options.map((opt) => (
                            <button
                                key={opt.id}
                                onClick={() => onOptionSelect(opt.id)}
                                style={{
                                    padding: 'var(--space-2) var(--space-3)',
                                    borderRadius: 'var(--radius-full)',
                                    border: '1px solid var(--color-border)',
                                    background: 'var(--color-bg-primary)',
                                    cursor: 'pointer',
                                    color: 'var(--color-text-primary)',
                                    fontSize: 'var(--font-size-sm)'
                                }}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                )}

                {/* Review Summary */}
                {message.type === 'review' && campaignConfig && (
                    <ReviewSummary
                        config={campaignConfig}
                        projection={projection}
                        validation={validation}
                        onLaunch={onLaunch}
                        launching={launching}
                    />
                )}

                {/* Success Message */}
                {message.type === 'success' && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        marginTop: 'var(--space-3)',
                        padding: 'var(--space-3)',
                        background: 'rgba(34, 197, 94, 0.1)',
                        borderRadius: 'var(--radius-md)'
                    }}>
                        <CheckCircle size={20} style={{ color: 'var(--color-success)' }} />
                        <span style={{ color: 'var(--color-success)', fontWeight: 500 }}>Campaign Created!</span>
                    </div>
                )}

                {/* Error Message */}
                {message.type === 'error' && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        marginTop: 'var(--space-3)',
                        padding: 'var(--space-3)',
                        background: 'rgba(239, 68, 68, 0.1)',
                        borderRadius: 'var(--radius-md)'
                    }}>
                        <AlertCircle size={20} style={{ color: 'var(--color-error)' }} />
                        <span style={{ color: 'var(--color-error)', fontWeight: 500 }}>Error</span>
                    </div>
                )}
            </div>
        </div>
    );
};

// Objective Selection Card
const ObjectiveCard = ({ objective, onSelect }) => {
    const Icon = objective.icon;

    return (
        <button
            onClick={onSelect}
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 'var(--space-2)',
                padding: 'var(--space-4)',
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-lg)',
                cursor: 'pointer',
                transition: 'all var(--transition-fast)',
                textAlign: 'center'
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--color-primary)';
                e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--color-border)';
                e.currentTarget.style.transform = 'translateY(0)';
            }}
        >
            <div style={{
                width: '48px',
                height: '48px',
                borderRadius: 'var(--radius-full)',
                background: 'var(--gradient-primary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <Icon size={24} style={{ color: 'white' }} />
            </div>
            <div>
                <div style={{
                    fontWeight: 600,
                    color: 'var(--color-text-primary)',
                    marginBottom: '4px'
                }}>
                    {objective.label}
                </div>
                <div style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-tertiary)'
                }}>
                    {objective.description}
                </div>
            </div>
        </button>
    );
};

// Review Summary Component
const ReviewSummary = ({ config, projection, validation, onLaunch, launching }) => {
    return (
        <div style={{
            marginTop: 'var(--space-4)',
            background: 'var(--color-bg-primary)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-4)'
        }}>
            <h3 style={{
                margin: '0 0 var(--space-3) 0',
                fontSize: 'var(--font-size-md)',
                fontWeight: 600,
                color: 'var(--color-text-primary)'
            }}>
                Campaign Summary
            </h3>

            <div style={{
                display: 'grid',
                gap: 'var(--space-3)'
            }}>
                <SummaryRow
                    icon={Target}
                    label="Objective"
                    value={OBJECTIVES.find(o => o.id === config.objective)?.label || 'Not set'}
                />
                <SummaryRow
                    icon={DollarSign}
                    label="Daily Budget"
                    value={formatCurrency(config.budget.amount)}
                />
                <SummaryRow
                    icon={Globe}
                    label="Target Countries"
                    value={config.targeting.locations.countries.length > 0
                        ? config.targeting.locations.countries.join(', ')
                        : 'Global'}
                />
                <SummaryRow
                    icon={Layers}
                    label="Placements"
                    value={config.placements === 'automatic' ? 'Automatic (Advantage+)' : 'Manual'}
                />
            </div>

            {/* Projected Performance */}
            {projection && (
                <div style={{
                    marginTop: 'var(--space-4)',
                    padding: 'var(--space-3)',
                    background: 'rgba(124, 58, 237, 0.05)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid rgba(124, 58, 237, 0.1)'
                }}>
                    <div style={{
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 500,
                        color: 'var(--color-primary)',
                        marginBottom: 'var(--space-2)'
                    }}>
                        Estimated Monthly Performance
                    </div>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: 'var(--space-3)'
                    }}>
                        <div>
                            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                                Impressions
                            </div>
                            <div style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
                                {projection.total.impressions.toLocaleString()}
                            </div>
                        </div>
                        <div>
                            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                                Clicks
                            </div>
                            <div style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
                                {projection.total.clicks.toLocaleString()}
                            </div>
                        </div>
                        <div>
                            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                                Est. CPA
                            </div>
                            <div style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
                                ${projection.total.estimatedCPA}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Validation Warnings */}
            {validation.warnings.length > 0 && (
                <div style={{
                    marginTop: 'var(--space-3)',
                    padding: 'var(--space-3)',
                    background: 'rgba(234, 179, 8, 0.1)',
                    borderRadius: 'var(--radius-md)'
                }}>
                    {validation.warnings.map((warning, i) => (
                        <div key={i} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            color: 'rgb(234, 179, 8)',
                            fontSize: 'var(--font-size-sm)'
                        }}>
                            <AlertCircle size={14} />
                            {warning}
                        </div>
                    ))}
                </div>
            )}

            {/* Launch Button */}
            <button
                onClick={onLaunch}
                disabled={launching || validation.errors.length > 0}
                style={{
                    width: '100%',
                    marginTop: 'var(--space-4)',
                    padding: 'var(--space-3) var(--space-4)',
                    background: launching || validation.errors.length > 0
                        ? 'var(--color-surface)'
                        : 'var(--gradient-primary)',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    color: launching || validation.errors.length > 0
                        ? 'var(--color-text-tertiary)'
                        : 'white',
                    fontWeight: 600,
                    fontSize: 'var(--font-size-md)',
                    cursor: launching || validation.errors.length > 0 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 'var(--space-2)'
                }}
            >
                {launching ? (
                    <>
                        <Loader2 size={18} className="spin" />
                        Launching...
                    </>
                ) : (
                    <>
                        <Rocket size={18} />
                        Launch Campaign
                    </>
                )}
            </button>
        </div>
    );
};

// Summary Row Component
const SummaryRow = ({ icon: Icon, label, value }) => (
    <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)'
    }}>
        <Icon size={16} style={{ color: 'var(--color-text-tertiary)' }} />
        <span style={{
            flex: 1,
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)'
        }}>
            {label}
        </span>
        <span style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 500,
            color: 'var(--color-text-primary)'
        }}>
            {value}
        </span>
    </div>
);

export default CampaignBuilder;
